import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ClinicProductAvailability {
  id: string;
  clinic_id: string;
  product_id: string;
  available: boolean;
  unavailable_reason: string | null;
}

export const useClinicProductAvailability = () => {
  return useQuery({
    queryKey: ["clinic-product-availability"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clinic_product_availability")
        .select("*");

      if (error) throw error;
      return data as ClinicProductAvailability[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const getUnavailableProducts = (
  clinicId: string,
  selectedProducts: string[],
  availabilityData: ClinicProductAvailability[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  products: any[],
) => {
  if (!availabilityData || !products) return [];
  if (!selectedProducts || selectedProducts.length === 0) return [];

  // Convert all selected products to internal UUIDs for comparison
  // Filter out any undefined/null values first
  const selectedInternalIds = selectedProducts
    .filter(Boolean) // Remove undefined/null values
    .map((productId) => {
      // If it's already an internal UUID, return as is
      if (productId.includes("-")) return productId;

      // If it's a Stripe ID, find the corresponding internal UUID
      const product = products.find((p) => p.stripe_product_id === productId);
      return product ? product.id : productId;
    })
    .filter(Boolean); // Filter again to remove any that didn't match

  return selectedInternalIds.filter((productId) => {
    const availability = availabilityData.find(
      (item) => item.clinic_id === clinicId && item.product_id === productId,
    );
    // If no record exists, assume available. If record exists, check the available flag
    return availability ? !availability.available : false;
  });
};

export const getUnavailableProductsWithReasons = (
  clinicId: string,
  selectedProducts: string[],
  availabilityData: ClinicProductAvailability[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  products: any[],
) => {
  if (!availabilityData || !products) return [];

  console.log("getUnavailableProductsWithReasons - clinicId:", clinicId);
  console.log(
    "getUnavailableProductsWithReasons - selectedProducts:",
    selectedProducts,
  );
  console.log(
    "getUnavailableProductsWithReasons - availabilityData:",
    availabilityData,
  );

  // Convert all selected products to internal UUIDs for comparison
  const selectedProductsWithInternalIds = selectedProducts
    .map((productId) => {
      // If it's already an internal UUID, return as is
      if (productId.includes("-")) {
        const product = products.find((p) => p.id === productId);
        return { originalId: productId, internalId: productId, product };
      }

      // If it's a Stripe ID, find the corresponding internal UUID
      const product = products.find((p) => p.stripe_product_id === productId);
      return { originalId: productId, internalId: product?.id, product };
    })
    .filter((item) => item.internalId && item.product);

  console.log(
    "getUnavailableProductsWithReasons - selectedProductsWithInternalIds:",
    selectedProductsWithInternalIds,
  );

  const unavailableProducts = selectedProductsWithInternalIds.filter((item) => {
    const availability = availabilityData.find(
      (avail) =>
        avail.clinic_id === clinicId && avail.product_id === item.internalId,
    );
    console.log(
      `Checking product ${item.product.name} (${item.internalId}) at clinic ${clinicId} - availability:`,
      availability,
    );
    // If no record exists, assume available. If record exists, check the available flag
    return availability ? !availability.available : false;
  });

  console.log(
    "getUnavailableProductsWithReasons - unavailableProducts:",
    unavailableProducts,
  );

  return unavailableProducts.map((item) => {
    const availability = availabilityData.find(
      (avail) =>
        avail.clinic_id === clinicId && avail.product_id === item.internalId,
    );

    return {
      id: item.originalId, // Use original ID for removal
      name: item.product?.name,
      reason:
        availability?.unavailable_reason || "Not available at this location",
    };
  });
};
